import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import LifeBridge from '@tnlife/tn-bridge';
import { setDarkTheme } from '@life_uikit/uikit/helpers';
import { me as fetchMe, lifeAuth, logout as apiLogout } from '@/shared/api/auth';
import { UnauthorizedError } from '@/shared/api/base';
import type { Me } from '@/domain/types';

export const useAuthStore = defineStore('auth', () => {
  const me = ref<Me | null>(null);
  // true с рождения: route-guard ждёт окончания первичной инициализации.
  // Иначе стартовая навигация резолвится ДО bootstrapAuth (в мини-аппе его
  // задерживает LifeMiniAppInit) -> guard видит me=null -> /login -> петля
  // перезагрузок через login.vue (location.href=/auth/login).
  const loading = ref(true);
  const error = ref('');

  const isAuthenticated = computed(() => me.value !== null);

  function isMiniApp(): boolean {
    return LifeBridge.isIFrame || LifeBridge.isWebView;
  }

  /**
   * Инициализация авторизации при старте приложения.
   *
   * Режим мини-аппа (TN Life iframe/WebView):
   *   1. Получаем authCode через мост.
   *   2. Меняем его на сессию через POST /auth/life.
   *
   * Standalone-режим:
   *   1. Запрашиваем GET /api/v1/me.
   *   2. 401 → me=null (показываем экран «Войдите»), НЕ редиректим автоматически.
   *
   * В любом случае монтирование не блокируем — приложение должно подняться.
   */
  async function bootstrapAuth(): Promise<void> {
    // Подписка на смену темы от хоста (работает и в standalone, и в iframe)
    LifeBridge.addEventListener('colorSchemeUpdated', setDarkTheme);

    loading.value = true;
    error.value = '';
    try {
      if (isMiniApp()) {
        // Получаем auth-code от суперапа
        let authCode = '';
        try {
          const res = await LifeBridge.send('LifeMiniAppAuthCode', {});
          authCode =
            (res as any)?.data?.code ??
            (res as any)?.data?.authCode ??
            (res as any)?.code ??
            (res as any)?.authCode ??
            '';
        } catch {
          error.value = 'Не удалось получить код авторизации от TN Life';
          return;
        }

        if (!authCode) {
          error.value = 'TN Life вернул пустой authCode';
          return;
        }

        const { user } = await lifeAuth(authCode);
        me.value = user;
      } else {
        // Standalone: проверяем активную сессию
        try {
          me.value = await fetchMe();
        } catch (e) {
          if (e instanceof UnauthorizedError) {
            me.value = null; // покажем экран «Войдите»
          } else {
            throw e;
          }
        }
      }
    } catch (e) {
      error.value = String(e);
    } finally {
      loading.value = false;
    }
  }

  /**
   * Logout — завершить сессию на сервере и очистить локальное состояние.
   */
  async function logout(): Promise<void> {
    try {
      await apiLogout();
    } catch {
      // Даже при ошибке сети — очищаем состояние локально
    } finally {
      me.value = null;
      // В standalone после логаута переходим на главную
      if (!isMiniApp()) {
        window.location.href = '/';
      }
    }
  }

  return {
    me,
    loading,
    error,
    isAuthenticated,
    bootstrapAuth,
    logout,
  };
});
